/**
 * OnboardingProgress - 4-step checklist shown to new users and visitors
 * Tracks: signed up, spotted, favorited, messaged
 * - Authenticated: auto-marks signed_up, shows live completion progress
 * - Visitors: shows checklist as teaser with auth-required steps locked
 *
 * Minimize (chevron): collapses to title row only. Persists in sessionStorage —
 *   resets on next browser session so the checklist comes back.
 * Close (×): hides for this session only (sessionStorage). Returns on next session.
 *   Steps completion (localStorage) is never erased by either action.
 *
 * Each step is clickable and guides users to the relevant feature.
 *
 * @atomic @idempotent
 */
import { memo, useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { createLogger } from '../../utils/logger'
import { trackEvent } from '../../utils/analytics'
import { useAuth } from '../../context/AuthContext'
import { useImmersive } from '../../context/ImmersiveContext'
import { useMapSpot } from '../../context/MapSpotContext'
import { useOnboardingStepNavigation } from '../../hooks/useOnboardingStepNavigation'
import OnboardingHeader from './OnboardingHeader'
import OnboardingStepButton from './OnboardingStepButton'

const logger = createLogger('OnboardingProgress')

const STORAGE_KEY        = 'onboarding_progress_v1'
// Track for this user ('visitor' | 'vendor') — written by hydration hook
export const ONBOARDING_TRACK_KEY = 'onboarding_track_v1'
// Session-scoped keys — both reset when the browser session ends
const SESSION_CLOSED_KEY   = 'onboarding_progress_closed_session'
const SESSION_MINIMIZED_KEY = 'onboarding_progress_minimized_session'

const STEPS = ['signed_up', 'spotted', 'favorited', 'messaged']
// Steps shown as locked for visitors (require an account)
const AUTH_REQUIRED_STEPS = new Set(['spotted', 'favorited', 'messaged'])

export function isOnboardingSessionClosed() {
  try { return sessionStorage.getItem(SESSION_CLOSED_KEY) === '1' } catch { return false }
}

export function getOnboardingProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

// Custom event so the same-tab component re-renders immediately when a step is marked
const STEP_EVENT = 'onboarding:step_completed'

/**
 * Mark a step as complete. `ts` is optional — defaults to Date.now().
 * Used both for real-time in-session actions and server hydration.
 */
export function markOnboardingStep(step, ts) {
  try {
    const current = getOnboardingProgress()
    if (!current[step]) {
      current[step] = ts ?? Date.now()
      localStorage.setItem(STORAGE_KEY, JSON.stringify(current))
      trackEvent('Onboarding', 'step_completed', step)
      window.dispatchEvent(new CustomEvent(STEP_EVENT, { detail: { step } }))
    }
  } catch (e) {
    logger.warn('Could not persist onboarding step', e)
  }
}

/**
 * Bulk-mark multiple steps at once and dispatch a single STEP_EVENT.
 * Use this instead of calling markOnboardingStep in a loop to avoid
 * triggering N re-renders (one per step).
 *
 * @param {Record<string, number|boolean>} stepsObj - { step: timestamp }
 */
export function bulkMarkOnboardingSteps(stepsObj) {
  if (!stepsObj || typeof stepsObj !== 'object') return
  try {
    const current = getOnboardingProgress()
    let changed = false
    for (const [step, ts] of Object.entries(stepsObj)) {
      if (!current[step] && ts) {
        current[step] = typeof ts === 'number' ? ts : Date.now()
        trackEvent('Onboarding', 'step_completed', step)
        changed = true
      }
    }
    if (changed) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(current))
      window.dispatchEvent(new CustomEvent(STEP_EVENT, { detail: { bulk: true } }))
    }
  } catch (e) {
    logger.warn('Could not bulk-persist onboarding steps', e)
  }
}

/**
 * Overwrite localStorage with the server-canonical step set.
 *
 * Unlike bulkMarkOnboardingSteps (which is additive), this replaces the local
 * record with the server's authoritative state so admin resets propagate correctly.
 * Any steps that exist locally but NOT on the server are preserved in the merged
 * result — the caller is responsible for flushing those back to the server.
 *
 * @param {Record<string, number>} serverSteps - Canonical steps from GET /profile/onboarding
 * @returns {Record<string, number>} The previous local progress (for flush diffing)
 */
export function setOnboardingProgressFromServer(serverSteps) {
  if (!serverSteps || typeof serverSteps !== 'object') return {}
  try {
    const previousLocal = getOnboardingProgress()
    // Start from server state, then layer in local-only steps
    const merged = { ...serverSteps }
    for (const [step, ts] of Object.entries(previousLocal)) {
      if (!merged[step] && ts) merged[step] = ts
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
    window.dispatchEvent(new CustomEvent(STEP_EVENT, { detail: { bulk: true } }))
    return previousLocal
  } catch (e) {
    logger.warn('Could not set onboarding progress from server', e)
    return {}
  }
}

const OnboardingProgress = memo(function OnboardingProgress({ onDismiss, onTabChange }) {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { activateSpotMode } = useMapSpot()
  
  // Use passed onTabChange if available, otherwise fall back to context
  const { changeTab: contextChangeTab } = useImmersive()
  const changeTab = onTabChange || contextChangeTab
  
  const [progress, setProgress] = useState(getOnboardingProgress)
  const [track, setTrack] = useState(() => {
    try { return localStorage.getItem(ONBOARDING_TRACK_KEY) || 'visitor' } catch { return 'visitor' }
  })

  // Session-scoped closed state — banner returns on next browser session
  const [closed, setClosed] = useState(() => {
    try { return sessionStorage.getItem(SESSION_CLOSED_KEY) === '1' } catch { return false }
  })

  // Session-scoped minimized state — defaults to minimized on first visit each session
  const [minimized, setMinimized] = useState(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_MINIMIZED_KEY)
      return stored === null ? true : stored === '1'
    } catch { return true }
  })

  // Auto-mark signed_up for existing users who registered before this feature existed
  useEffect(() => {
    if (isAuthenticated && !progress.signed_up) {
      markOnboardingStep('signed_up')
      setProgress(getOnboardingProgress())
    }
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const refresh = () => setProgress(getOnboardingProgress())
    const refreshTrack = (e) => setTrack(e.detail || 'visitor')
    // 'storage' fires from other tabs; STEP_EVENT fires from same tab
    window.addEventListener('storage', refresh)
    window.addEventListener(STEP_EVENT, refresh)
    window.addEventListener('onboarding:track_changed', refreshTrack)
    return () => {
      window.removeEventListener('storage', refresh)
      window.removeEventListener(STEP_EVENT, refresh)
      window.removeEventListener('onboarding:track_changed', refreshTrack)
    }
  }, [])

  const completedCount = STEPS.filter((s) => progress[s]).length
  const stepsNamespace = track === 'vendor' ? 'vendor_steps' : 'steps'

  // Delegate step-click routing to dedicated hook
  const handleStepClick = useOnboardingStepNavigation({ progress, isAuthenticated, changeTab, activateSpotMode })

  const handleClose = useCallback(() => {
    try { sessionStorage.setItem(SESSION_CLOSED_KEY, '1') } catch { /* noop */ }
    setClosed(true)
    trackEvent('Onboarding', 'progress_closed_session', `${completedCount}_of_${STEPS.length}`)
    onDismiss?.()
  }, [completedCount, onDismiss])

  const handleToggleMinimize = useCallback(() => {
    setMinimized(prev => {
      const next = !prev
      try { sessionStorage.setItem(SESSION_MINIMIZED_KEY, next ? '1' : '0') } catch { /* noop */ }
      trackEvent('Onboarding', next ? 'progress_minimized' : 'progress_expanded', `${completedCount}_of_${STEPS.length}`)
      return next
    })
  }, [completedCount])

  // Hide if closed this session
  if (closed) return null

  // Show completion card when all steps are done (authenticated users only)
  if (isAuthenticated && completedCount === STEPS.length) {
    return (
      <div className="bg-white/95 backdrop-blur-sm border border-primary/30 rounded-xl shadow-md overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3">
          <CheckCircleIcon className="h-7 w-7 text-primary flex-shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900">{t('onboarding.welcome.progress.completed_title')}</p>
            <p className="text-xs text-gray-500 mt-0.5">{t('onboarding.welcome.progress.completed_subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            onTouchEnd={(e) => { e.preventDefault(); handleClose() }}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors focus:outline-none"
            aria-label={t('common.close')}
          >
            <XMarkIcon className="h-4 w-4 pointer-events-none" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white/95 backdrop-blur-sm border border-primary/30 rounded-xl shadow-md overflow-hidden">
      <OnboardingHeader
        completedCount={completedCount}
        totalSteps={STEPS.length}
        minimized={minimized}
        onToggleMinimize={handleToggleMinimize}
        onClose={handleClose}
      />

      {/* Collapsible body */}
      {!minimized && (
        <div className="px-4 pb-4">
          {/* Progress bar */}
          <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${(completedCount / STEPS.length) * 100}%` }}
            />
          </div>

          <p className="text-xs text-gray-500 mb-3">
            {isAuthenticated
              ? t('onboarding.welcome.progress.subtitle', { count: completedCount, total: STEPS.length })
              : t('onboarding.welcome.progress.visitor_subtitle')}
          </p>

          <ul className="space-y-2">
            {STEPS.map((step) => {
              const isDone = !!progress[step]
              const isLocked = !isAuthenticated && AUTH_REQUIRED_STEPS.has(step)
              
              return (
                <OnboardingStepButton
                  key={step}
                  step={step}
                  isDone={isDone}
                  isLocked={isLocked}
                  onClick={handleStepClick}
                  stepsNamespace={stepsNamespace}
                />
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
})

export default OnboardingProgress
